import React, { useEffect, useRef, useState } from "react";
import "./report.scss";
import NoData from "./NoData";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TableTagFilterDropdown from "./TableTagFilterDropdown";
import { toFormatPrice } from "../helpers/function";
import { useTranslation } from "react-i18next";

// ─── Helpers ───────────────────────────────────────────────────────────────────
function formatNum(n, fractionDigits = null) {
  if (n == null) return "";
  const parsed = Number(n);
  if (!Number.isFinite(parsed)) return "";
  if (typeof fractionDigits === "number") {
    return parsed.toLocaleString(undefined, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  }
  return parsed.toLocaleString();
}

function isNumericValue(val) {
  if (typeof val === "number") return Number.isFinite(val);
  if (typeof val !== "string") return false;
  const normalized = val.replace(/,/g, "").trim();
  if (!normalized) return false;
  return /^-?\d+(\.\d+)?$/.test(normalized);
}

function getColumnFractionDigits(col, fractionDigitsByColumn = {}) {
  if (Object.prototype.hasOwnProperty.call(fractionDigitsByColumn, col?.key)) {
    return fractionDigitsByColumn[col.key];
  }
  const key = String(col?.key ?? "");
  const label = String(col?.label ?? "");
  const isPriceColumn =
    col?.isPriceColumn === true || /price/i.test(key) || /price/i.test(label);
  return isPriceColumn ? 2 : null;
}

function isPriceColumn(col) {
  const key = String(col?.key ?? "");
  const label = String(col?.label ?? "");
  return (
    col?.isPriceColumn === true || /price/i.test(key) || /price/i.test(label)
  );
}

const DEFAULT_COLUMNS = [
  { key: "_index", label: "#", align: "center", width: 40 },
  { key: "location", label: "Location", align: "left", width: 200 },
  { key: "target", label: "Target", align: "right", width: 130 },
  { key: "sales", label: "Sales", align: "right", width: 150 },
  { key: "cost", label: "Cost", align: "right", width: 120 },
  { key: "profit", label: "Profit", align: "right", width: 120 },
  { key: "qty", label: "Qty", align: "right", width: 100 },
  { key: "noOfInvoice", label: "No. of Invoice", align: "right", width: 150 },
  { key: "noOfCustomer", label: "No. of Customer", align: "right", width: 150 },
  { key: "newCustomer", label: "New Customer", align: "right", width: 150 },
];

export default function DataTable({
  columns = DEFAULT_COLUMNS,
  data = [],
  totals = null,
  showSearch = true,
  showDateRange = true,
  pageLimit = 10,
  totalRecords = 0,
  renderCell,
  fractionDigitsByColumn = {},
  fetchData,
  currentPage = 1,
  onPageChange,
  isRowExpanded,
  renderExpandedRow,
}) {
  const { t } = useTranslation();
  const isControlled = typeof onPageChange === "function";

  const [internalPage, setInternalPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageLimit);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const sortDropdownRefs = useRef({});
  const [searchTerms, setSearchTerms] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [draftFilters, setDraftFilters] = useState({});

  useEffect(() => {
    setPageSize(pageLimit);
  }, [pageLimit]);

  useEffect(() => {
    if (isControlled) setInternalPage(currentPage);
  }, [currentPage, isControlled]);

  const page = isControlled ? currentPage : internalPage;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  useEffect(() => {
    if (page > totalPages && totalPages > 0) goToPage(1);
  }, [totalRecords]);

  const filteredData = data.filter((row) => {
    const activeFilterKeys = Object.keys(appliedFilters);
    if (!activeFilterKeys.length) return true;
    return activeFilterKeys.every((key) => {
      const selected = appliedFilters[key] || [];
      if (!selected.length) return true;
      const value = row[key];
      const normalizedValue = value == null ? "" : String(value);
      return selected.includes(normalizedValue);
    });
  });

  const sortedData = sortCol
    ? [...filteredData].sort((a, b) => {
        const va = a[sortCol] ?? "";
        const vb = b[sortCol] ?? "";
        const na = parseFloat(String(va).replace(/,/g, ""));
        const nb = parseFloat(String(vb).replace(/,/g, ""));
        const isNum = !isNaN(na) && !isNaN(nb);
        const cmp = isNum ? na - nb : String(va).localeCompare(String(vb));
        return sortDir === "asc" ? cmp : -cmp;
      })
    : filteredData;
  const paged = sortedData;

  function handleSortFromDropdown({ key, value }) {
    if (!key || key === "_index") return;
    setSortCol(key);
    setSortDir(value === -1 ? "desc" : "asc");
  }

  function handleSortDropdownHide(key) {
    const dropdownElement = sortDropdownRefs.current[key];
    if (dropdownElement) dropdownElement.classList.remove("show");
  }

  function getAllColumnOptions(key) {
    return [
      ...new Set(
        data.map((item) => (item?.[key] == null ? "" : String(item[key]))),
      ),
    ];
  }

  function getVisibleColumnOptions(key) {
    const allOptions = getAllColumnOptions(key);
    const term = (searchTerms[key] || "").toLowerCase().trim();
    if (!term) return allOptions;
    return allOptions.filter((option) => option.toLowerCase().includes(term));
  }

  function getCurrentSelectedValues(key) {
    if (draftFilters[key]) return draftFilters[key];
    return appliedFilters[key] || [];
  }

  function handleSearchChange(event, key) {
    const value = event?.target?.value || "";
    setSearchTerms((prev) => ({ ...prev, [key]: value }));
  }

  function handleSelectAllChange(event, key) {
    const visibleOptions = getVisibleColumnOptions(key);
    const updated = event.target.checked ? visibleOptions : [];
    setDraftFilters((prev) => ({ ...prev, [key]: updated }));
  }

  function handleItemFilterChange(event, item, key) {
    const current = getCurrentSelectedValues(key);
    const updated = event.target.checked
      ? [...new Set([...current, item])]
      : current.filter((value) => value !== item);
    setDraftFilters((prev) => ({ ...prev, [key]: updated }));
  }

  function handleClearFilter(key) {
    setSearchTerms((prev) => ({ ...prev, [key]: "" }));
    setDraftFilters((prev) => ({ ...prev, [key]: [] }));
    setAppliedFilters((prev) => ({ ...prev, [key]: [] }));
    handleSortDropdownHide(key);
  }

  function handleApplyFilter(key) {
    const selected = getCurrentSelectedValues(key);
    setAppliedFilters((prev) => ({ ...prev, [key]: selected }));
    handleSortDropdownHide(key);
  }

  function goToPage(newPage, newSize = pageSize) {
    if (!isControlled) setInternalPage(newPage);
    else onPageChange?.(newPage);
    fetchData({ page: newPage, limit: newSize });
  }

  // useEffect(() => {
  //   if (page > totalPages && totalPages > 0) {
  //     goToPage(1);
  //   }
  // }, [totalRecords]);

  // ── Default cell renderer
  function defaultRenderCell(col, row) {
    const columnFractionDigits = getColumnFractionDigits(
      col,
      fractionDigitsByColumn,
    );
    if (col.key === "_index") return row._rowIndex;
    if (col.key === "sales") {
      const val = row[col.key];
      const pct = row._salesPct ?? "";
      const isNeg = row._salesNeg ?? false;
      return (
        <span className={isNeg ? "dtbs-sales-negative" : "dtbs-sales-positive"}>
          {formatNum(val, columnFractionDigits)}
          {pct ? ` (${pct})` : ""}
        </span>
      );
    }
    const val = row[col.key];
    if (isNumericValue(val)) {
      if (isPriceColumn(col)) {
        return toFormatPrice(Number(val));
      }
      return formatNum(val, columnFractionDigits);
    }
    return val ?? "";
  }

  const thClass = (align) =>
    align === "right"
      ? "dtbs-th-right"
      : align === "center"
        ? "dtbs-th-center"
        : "dtbs-th-left";

  const tdClass = (align) =>
    align === "right"
      ? "dtbs-td-right"
      : align === "center"
        ? "dtbs-td-center"
        : "dtbs-td-left";

  const rangeStart = totalRecords === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalRecords);

  return (
    <div className="dtbs-wrapper">
      {/* Table */}
      <div className="dtbs-table-container">
        <table className="dtbs-table">
          <colgroup>
            {columns.map((col) => (
              <col key={col.key} style={{ width: col.width ?? "auto" }} />
            ))}
          </colgroup>

          <thead>
            {/* Header row */}
            <tr>
              {columns.map((col) => (
                <th key={col.key} className={thClass(col.align)}>
                  {t(col.label)}
                  {col.key !== "_index" && col.showFilter !== false && (
                    <TableTagFilterDropdown
                      filterKey={col.key}
                      dropdownRef={(el) => {
                        sortDropdownRefs.current[col.key] = el;
                      }}
                      renderInPortal
                      selectedValues={getCurrentSelectedValues(col.key)}
                      options={getVisibleColumnOptions(col.key)}
                      searchValue={searchTerms[col.key] || ""}
                      onSortChange={handleSortFromDropdown}
                      onHide={handleSortDropdownHide}
                      onSearchKeyPress={handleSearchChange}
                      onSearchChange={handleSearchChange}
                      onSelectAllChange={handleSelectAllChange}
                      onItemChange={handleItemFilterChange}
                      onClear={() => handleClearFilter(col.key)}
                      onApply={() => handleApplyFilter(col.key)}
                    />
                  )}
                </th>
              ))}
            </tr>

            {/* Totals row */}
            {totals && (
              <tr>
                {columns.map((col) => {
                  if (col.key === "_index") {
                    return (
                      <td
                        key={col.key}
                        className="dtbs-total-cell dtbs-td-left"
                      >
                        {t("Total")}
                      </td>
                    );
                  }
                  return (
                    <td
                      key={col.key}
                      className={`dtbs-total-cell dtbs-total-cell-${col.align}`}
                    >
                      {totals[col.key] != null
                        ? isPriceColumn(col)
                          ? toFormatPrice(Number(totals[col.key]))
                          : formatNum(
                              totals[col.key],
                              getColumnFractionDigits(
                                col,
                                fractionDigitsByColumn,
                              ),
                            )
                        : ""}
                    </td>
                  );
                })}
              </tr>
            )}
          </thead>

          {/* Body */}
          {paged && paged.length > 0 ? (
            <tbody>
              {paged.map((row, idx) => {
                // _rowIndex is absolute across all pages
                const rowWithIndex = {
                  ...row,
                  _rowIndex: (page - 1) * pageSize + idx + 1,
                };
                const stripeIndex =
                  typeof rowWithIndex._stripeIndex === "number"
                    ? rowWithIndex._stripeIndex
                    : idx;
                const rowKey = rowWithIndex._rowKey || idx;
                const expanded = !!isRowExpanded?.(rowWithIndex);
                return (
                  <React.Fragment key={rowKey}>
                    <tr
                      className={
                        stripeIndex % 2 !== 0 ? "dtbs-row-odd" : "dtbs-row-even"
                      }
                      style={
                        rowWithIndex._isChild
                          ? { background: "#fff" }
                          : undefined
                      }
                    >
                      {columns.map((col) => (
                        <td key={col.key} className={tdClass(col.align)}>
                          {renderCell
                            ? (renderCell(col, rowWithIndex) ??
                              defaultRenderCell(col, rowWithIndex))
                            : defaultRenderCell(col, rowWithIndex)}
                        </td>
                      ))}
                    </tr>
                    {expanded && renderExpandedRow && (
                      <tr
                        className="dtbs-expanded-row"
                        style={{ backgroundColor: "#FAFAFA" }}
                      >
                        <td
                          colSpan={columns.length}
                          style={{ padding: "0px 0px 0px 52px" }}
                        >
                          {renderExpandedRow(rowWithIndex)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          ) : (
            <tbody>
              <tr>
                <td colSpan={columns.length} style={{ padding: "0" }}>
                  <NoData />
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>

      {/* Pagination */}
      {paged && paged.length > 0 && (
        <div className="dtbs-pagination">
          <span className="dtbs-page-info">
            {/* {totalRecords === 0
              ? "0 results"
              : `${rangeStart}–${rangeEnd} of ${totalRecords}`} */}
          </span>

          <div className="dtbs-page-controls">
            {/* <span className="dtbs-page-label">Rows per page:</span> */}
            <span
              style={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <select
                className="dtbs-page-select"
                value={pageSize}
                onChange={(e) => {
                  const newSize = Number(e.target.value);
                  setPageSize(newSize);
                  goToPage(1, newSize); // reset to page 1 on size change
                }}
                style={{
                  paddingRight: 24,
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                }}
              >
                {[10, 16, 25, 40, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <KeyboardArrowDownIcon
                fontSize="small"
                style={{
                  position: "absolute",
                  right: 6,
                  pointerEvents: "none",
                  color: "#8a8a8a",
                }}
              />
            </span>

            <span className="dtbs-page-text">
              {/* Page {page} of {totalPages} */}
              {page} of {totalPages}
            </span>

            {/* First page */}
            {/* <button
              className="dtbs-nav-btn"
              onClick={() => goToPage(1)}
              disabled={page === 1}
            >
              <KeyboardDoubleArrowLeftIcon />
            </button> */}

            {/* Prev page */}
            <button
              className="dtbs-nav-btn"
              onClick={() => goToPage(page - 1)}
              disabled={page === 1}
            >
              <KeyboardArrowLeftIcon />
            </button>

            {/* Next page */}
            <button
              className="dtbs-nav-btn"
              onClick={() => goToPage(page + 1)}
              disabled={page >= totalPages}
            >
              <KeyboardArrowRightIcon />
            </button>

            {/* Last page */}
            {/* <button
              className="dtbs-nav-btn"
              onClick={() => goToPage(totalPages)}
              disabled={page >= totalPages}
            >
              <KeyboardDoubleArrowRightIcon />
            </button> */}
          </div>
        </div>
      )}
    </div>
  );
}

export { DEFAULT_COLUMNS, formatNum };
